import { type MappedProductVariant, type ProductHit, type ProductVariants } from '@libs/algolia';
import { Cookie } from '@libs/common-next';
import type { ItemUI } from '@libs/common-node';
import { logger } from '@libs/core';

import type { AlgoliaClickEvent, AlgoliaTrackData } from '../types/algoliaTracking';

// Local type for products with productVariants property (like PDP Product type)
type ProductWithVariants = ProductHit & {
    productVariants?: ProductVariants;
};

// Union type alias for all supported product types
type SupportedProduct = ProductWithVariants | ProductHit | MappedProductVariant | ItemUI;

const COOKIE_EXPIRY_HOURS = 1 / 24; // 1 hour
const MINIMUM_CONSENT_GROUPS = 4;

export const getMasterProductID = (product: SupportedProduct) => {
    if ('masterProductID' in product && product.masterProductID) {
        return product.masterProductID;
    }
    if ('masterProductKey' in product && product.masterProductKey) {
        return Number(product.masterProductKey);
    }
    return -1; // Fallback to -1 if neither masterProductID nor masterProductKey is found
};

const extractObjectIDs = (product: SupportedProduct): string[] | undefined => {
    if ('productVariants' in product && product.productVariants?.variants) {
        return product.productVariants.variants.map((variant: { objectID: string }) => variant.objectID);
    }
    if ('objectID' in product) {
        return [product.objectID];
    }
    if ('sku' in product) {
        return [product.sku];
    }
    return undefined;
};

const findEventByObjectIDs = (events: AlgoliaClickEvent[], objectIDs: string[]): AlgoliaClickEvent | null => {
    for (const event of events) {
        if (objectIDs.some((id: string) => event.objectIDs.includes(id))) {
            return event;
        }
    }
    return null;
};

const findEventByMasterProductID = (events: AlgoliaClickEvent[], masterProductID: number): AlgoliaClickEvent | null => {
    for (const event of events) {
        if (event.masterProductID === masterProductID) {
            return event;
        }
    }
    return null;
};

export const getPDPEventData = (product: SupportedProduct): AlgoliaClickEvent | null => {
    const objectIDs = extractObjectIDs(product);
    const algoliaCookie = getAlgoliaEvents();

    if (!algoliaCookie) {
        return null;
    }

    if (objectIDs) {
        const eventByObjectIDs = findEventByObjectIDs(algoliaCookie, objectIDs);
        if (eventByObjectIDs) {
            return eventByObjectIDs;
        }
    }

    const masterProductID = getMasterProductID(product);
    return findEventByMasterProductID(algoliaCookie, masterProductID);
};

export const deleteEvents = () => {
    Cookie.remove('algoliaEvents');
};

export const getAlgoliaEvents = () => {
    const algoliaCookie = Cookie.get('algoliaEvents');
    if (algoliaCookie) {
        try {
            return JSON.parse(algoliaCookie) as AlgoliaClickEvent[];
        } catch (error) {
            logger.error('Error parsing algoliaEvents cookie', error);
            return [] as AlgoliaClickEvent[];
        }
    }
    return [] as AlgoliaClickEvent[];
};

export const removeProductAlgoliaEvent = (objectId: string) => {
    const algoliaEvents = getAlgoliaEvents();

    const updatedEvents = algoliaEvents
        .map(event => ({
            ...event,
            objectIDs: event.objectIDs.filter(id => id !== objectId),
        }))
        .filter(event => event.objectIDs.length > 0);
    Cookie.set('algoliaEvents', JSON.stringify(updatedEvents));
};

export const addUpdateAlgoliaEvents = (algoliaTrackData: AlgoliaTrackData, addedToCart: boolean) => {
    let localSearchEvents = getAlgoliaEvents();
    const search = localSearchEvents.filter(clickInfo => clickInfo.queryID === algoliaTrackData.__queryID);
    if (search.length !== 0) {
        if (!search[0].objectIDs.includes(algoliaTrackData.objectID)) {
            search[0].objectIDs.push(algoliaTrackData.objectID);
        }
        search[0].addedToCart = addedToCart;
        const searchClicks = localSearchEvents.filter(clickInfo => clickInfo.queryID !== algoliaTrackData.__queryID);
        searchClicks.push(search[0]);
        localSearchEvents = searchClicks;
    } else {
        const clickInfo = {
            objectIDs: [algoliaTrackData.objectID],
            queryID: algoliaTrackData.__queryID,
            addedToCart,
            masterProductID: algoliaTrackData.masterProductID,
        } as AlgoliaClickEvent;
        localSearchEvents.push(clickInfo);
    }

    Cookie.set('algoliaEvents', JSON.stringify(localSearchEvents), { expires: COOKIE_EXPIRY_HOURS });
};

export const isOptedOutTracking = () => {
    const activeGroups = (window.OnetrustActiveGroups ?? window.OptanonActiveGroups)?.split(',');
    return !activeGroups || activeGroups.length < MINIMUM_CONSENT_GROUPS;
};
